<template>
  <div class="g-page">
    <div class="content-container">
      <!-- HEADER -->
      <div class="header-row">
        <h2 class="page-title">
          <b-icon icon="cash-stack" class="title-icon"></b-icon>
          Kassa Hisoboti
        </h2>
        <div class="header-actions">
          <button class="kirim-btn" @click="openKirim()" title="Kassaga pul kiritish">
            <b-icon icon="plus-circle-fill"></b-icon> Kassaga kirim
          </button>
          <button class="refresh-btn" @click="loadData()" :title="'Yangilash'">
            <b-icon icon="arrow-repeat"></b-icon>
          </button>
        </div>
      </div>

      <!-- FILTER PANEL -->
      <div class="filter-panel">
        <div class="filter-grid">
          <div class="filter-cell">
            <label>Sana (boshi)</label>
            <input type="date" v-model="filters.date1" class="form-input" @change="loadData()" />
          </div>
          <div class="filter-cell">
            <label>Sana (oxiri)</label>
            <input type="date" v-model="filters.date2" class="form-input" @change="loadData()" />
          </div>
          <div class="filter-cell">
            <label>Sklad</label>
            <select v-model="filters.sklad_id" class="form-input" @change="loadData()">
              <option value="">Joriy sklad</option>
              <option value="all">Barcha skladlar</option>
              <option v-for="s in sklads" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div class="filter-cell">
            <label>Tezkor</label>
            <div class="quick-row">
              <button class="quick-btn" @click="setQuick('today')">Bugun</button>
              <button class="quick-btn" @click="setQuick('week')">Hafta</button>
              <button class="quick-btn" @click="setQuick('month')">Oy</button>
              <button class="quick-btn" @click="setQuick('all')">Hammasi</button>
            </div>
          </div>
        </div>
      </div>

      <!-- BIG BALANCE CARDS — joriy qoldiq (so'm + dollar alohida) -->
      <div class="balance-row" v-if="data">
        <div class="bal-card big sum">
          <div class="bal-label">💵 KASSADA SO'M (tanlangan davr)</div>
          <div class="bal-value">{{ formatMoney(data.balances.sum_balance) }}</div>
          <div class="bal-sub">Davr boshida 0 — Naqd + Plastik + Shot + boshqa so'mli turlar</div>
        </div>
        <div class="bal-card big dollar">
          <div class="bal-label">💲 KASSADA DOLLAR (tanlangan davr)</div>
          <div class="bal-value">{{ formatDollar(data.balances.dollar_balance) }}</div>
          <div class="bal-sub">Davr boshida 0 — Dollar to'lov turi</div>
        </div>
      </div>

      <!-- RANGE TOTALS — kirim/chiqim oraliq bo'yicha -->
      <div class="totals-row" v-if="data">
        <div class="t-card kirim">
          <div class="t-label">Oraliq kirim (so'm)</div>
          <div class="t-value">+{{ formatMoney(data.totals.kirim_sum) }}</div>
        </div>
        <div class="t-card chiqim">
          <div class="t-label">Oraliq chiqim (so'm)</div>
          <div class="t-value">-{{ formatMoney(data.totals.chiqim_sum) }}</div>
        </div>
        <div class="t-card net" :class="data.totals.net_sum >= 0 ? 'positive' : 'negative'">
          <div class="t-label">Sof o'zgarish (so'm)</div>
          <div class="t-value">{{ data.totals.net_sum >= 0 ? '+' : '' }}{{ formatMoney(data.totals.net_sum) }}</div>
        </div>
        <div class="t-card kirim dollar">
          <div class="t-label">Oraliq kirim ($)</div>
          <div class="t-value">+{{ formatDollar(data.totals.kirim_dollar) }}</div>
        </div>
        <div class="t-card chiqim dollar">
          <div class="t-label">Oraliq chiqim ($)</div>
          <div class="t-value">-{{ formatDollar(data.totals.chiqim_dollar) }}</div>
        </div>
        <div class="t-card net dollar" :class="data.totals.net_dollar >= 0 ? 'positive' : 'negative'">
          <div class="t-label">Sof o'zgarish ($)</div>
          <div class="t-value">{{ data.totals.net_dollar >= 0 ? '+' : '' }}{{ formatDollar(data.totals.net_dollar) }}</div>
        </div>
      </div>

      <!-- BY PAY TYPE -->
      <div class="section" v-if="data">
        <h3 class="section-title">📊 To'lov turi bo'yicha</h3>
        <table class="g-table">
          <thead>
            <tr>
              <th>To'lov turi</th>
              <th>Qoldiq (davr)</th>
              <th>Kirim (oraliq)</th>
              <th>Chiqim (oraliq)</th>
              <th>Sof o'zgarish</th>
              <th>Tranz.</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in data.balances.by_pay_type" :key="row.pay_type_id">
              <td>
                <span class="pay-badge" :class="row.is_dollar ? 'dollar' : 'sum'">
                  {{ row.is_dollar ? '$' : "₸" }} {{ row.name }}
                </span>
              </td>
              <td><b>{{ row.is_dollar ? formatDollar(row.current_balance) : formatMoney(row.current_balance) }}</b></td>
              <td class="positive">+{{ row.is_dollar ? formatDollar(row.kirim) : formatMoney(row.kirim) }}</td>
              <td class="negative">-{{ row.is_dollar ? formatDollar(row.chiqim) : formatMoney(row.chiqim) }}</td>
              <td :class="row.net >= 0 ? 'positive' : 'negative'">
                {{ row.net >= 0 ? '+' : '' }}{{ row.is_dollar ? formatDollar(row.net) : formatMoney(row.net) }}
              </td>
              <td class="muted">{{ row.kirim_count + row.chiqim_count }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- BY DOC TYPE — pul harakati sabablari -->
      <div class="section" v-if="data && data.by_doc_type.length">
        <h3 class="section-title">📁 Hujjat turi bo'yicha (sabab)</h3>
        <table class="g-table">
          <thead>
            <tr>
              <th>Hujjat turi</th>
              <th>Kirim (so'm)</th>
              <th>Chiqim (so'm)</th>
              <th>Kirim ($)</th>
              <th>Chiqim ($)</th>
              <th>Tranz.</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in data.by_doc_type" :key="row.doc_type">
              <td>{{ docTypeLabel(row.doc_type) }}</td>
              <td class="positive">+{{ formatMoney(row.kirim_sum) }}</td>
              <td class="negative">-{{ formatMoney(row.chiqim_sum) }}</td>
              <td class="positive">+{{ formatDollar(row.kirim_dollar) }}</td>
              <td class="negative">-{{ formatDollar(row.chiqim_dollar) }}</td>
              <td class="muted">{{ row.count }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- DAILY TIMELINE -->
      <div class="section" v-if="data && data.daily.length">
        <h3 class="section-title">📈 Kunlik dinamika</h3>
        <table class="g-table compact">
          <thead>
            <tr>
              <th>Sana</th>
              <th>Kirim (so'm)</th>
              <th>Chiqim (so'm)</th>
              <th>Kirim ($)</th>
              <th>Chiqim ($)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in data.daily" :key="row.date">
              <td>{{ row.date }}</td>
              <td class="positive">+{{ formatMoney(row.kirim_sum) }}</td>
              <td class="negative">-{{ formatMoney(row.chiqim_sum) }}</td>
              <td class="positive">+{{ formatDollar(row.kirim_dollar) }}</td>
              <td class="negative">-{{ formatDollar(row.chiqim_dollar) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="loading" class="loading-state">Yuklanmoqda...</div>
      <div v-else-if="data && !data.balances.by_pay_type.length" class="empty-state">
        Ma'lumot topilmadi
      </div>
    </div>

    <!-- KASSAGA KIRIM MODAL -->
    <div v-if="showKirim" class="kk-overlay" @click.self="showKirim = false">
      <div class="kk-modal">
        <div class="kk-head">
          <h3>💰 Kassaga kirim</h3>
          <button class="kk-close" @click="showKirim = false">&times;</button>
        </div>
        <div class="kk-body">
          <p class="kk-hint">
            Kassada pul yetmaganda naqd / plastik / dollar shaklida kirim qiling.
            Keyin xarajat va taminotchi to'lovlarini amalga oshirishingiz mumkin.
          </p>

          <div class="kk-row">
            <label>Sklad</label>
            <select v-model="kirim.sklad_id" class="kk-input">
              <option v-for="s in sklads" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>

          <div class="kk-row">
            <label>To'lov turi <span class="kk-req">*</span></label>
            <select v-model="kirim.pay_type_id" class="kk-input">
              <option :value="null" disabled>To'lov turini tanlang...</option>
              <option v-for="p in paymentTypes" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>

          <div class="kk-row">
            <label>Summa <span class="kk-req">*</span> <span v-if="isDollarPay" class="kk-cur">($)</span></label>
            <input type="number" min="0" v-model.number="kirim.summa" class="kk-input" placeholder="0" />
          </div>

          <div class="kk-row" v-if="isDollarPay">
            <label>Dollar kursi</label>
            <input type="number" min="0" v-model.number="kirim.dollar_rate" class="kk-input" placeholder="0" />
          </div>

          <div class="kk-row" v-if="kassaCategories.length">
            <label>Sabab (kategoriya)</label>
            <select v-model="kirim.kassa_category_id" class="kk-input">
              <option :value="null">— Tanlanmagan —</option>
              <option v-for="c in kassaCategories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>

          <div class="kk-row">
            <label>Izoh</label>
            <input type="text" v-model="kirim.comment" class="kk-input" placeholder="Masalan: egasidan kassaga to'ldirish" />
          </div>

          <div class="kk-total" v-if="kirim.summa > 0">
            Kassaga kirim:
            <b v-if="isDollarPay">{{ formatDollar(kirim.summa) }}</b>
            <b v-else>{{ formatMoney(kirim.summa) }}</b>
          </div>
        </div>
        <div class="kk-foot">
          <button class="kk-btn cancel" @click="showKirim = false" :disabled="saving">Bekor qilish</button>
          <button class="kk-btn save" @click="submitKirim()" :disabled="saving">
            {{ saving ? 'Saqlanmoqda...' : 'Kassaga kiritish' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KassaSummaryReport',
  data() {
    return {
      loading: false,
      sklads: [],
      filters: {
        date1: '',
        date2: '',
        sklad_id: '',
      },
      data: null,
      showKirim: false,
      saving: false,
      kassaCategories: [],
      paymentTypes: [],
      dollarRate: 0,
      kirim: {
        sklad_id: '',
        pay_type_id: null,
        summa: 0,
        dollar_rate: 0,
        kassa_category_id: null,
        comment: '',
      },
    };
  },
  computed: {
    isDollarPay() {
      // Dollar to'lov turi = id 3 (payType.utils.Dollar)
      return Number(this.kirim.pay_type_id) === 3;
    },
  },
  methods: {
    formatMoney(n) {
      const v = parseFloat(n || 0);
      return v.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + " so'm";
    },
    formatDollar(n) {
      const v = parseFloat(n || 0);
      return v.toLocaleString('en-US', { maximumFractionDigits: 2 }) + ' $';
    },
    docTypeLabel(t) {
      const map = {
        tolov: "To'lov",
        chiqim: 'Sotuv',
        kirim: 'Kirim faktura',
        rasxod: 'Xarajat',
        prixod: 'Kirim faktura',
        salary: 'Oylik',
        return_in: 'Vozvrat (mijoz)',
        return_out: 'Vozvrat (taminotchi)',
        revision: 'Reviziya',
        money_transfer: "Pul ko'chirish",
        kassa_order: 'Kassa orderi',
        initial_balance: 'Boshlang\'ich qoldiq',
      };
      return map[t] || t;
    },
    setQuick(kind) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const fmt = (d) => d.toISOString().slice(0, 10);
      if (kind === 'today') {
        this.filters.date1 = fmt(today);
        this.filters.date2 = fmt(today);
      } else if (kind === 'week') {
        const d1 = new Date(today); d1.setDate(d1.getDate() - 6);
        this.filters.date1 = fmt(d1);
        this.filters.date2 = fmt(today);
      } else if (kind === 'month') {
        const d1 = new Date(today.getFullYear(), today.getMonth(), 1);
        this.filters.date1 = fmt(d1);
        this.filters.date2 = fmt(today);
      } else {
        this.filters.date1 = '';
        this.filters.date2 = '';
      }
      this.loadData();
    },
    toUnix(dateStr, endOfDay = false) {
      if (!dateStr) return null;
      const d = new Date(dateStr);
      if (endOfDay) d.setHours(23, 59, 59, 999);
      else          d.setHours(0, 0, 0, 0);
      return Math.floor(d.getTime() / 1000);
    },
    async loadData() {
      this.loading = true;
      try {
        const params = {};
        const d1 = this.toUnix(this.filters.date1, false);
        const d2 = this.toUnix(this.filters.date2, true);
        if (d1) params.date1 = d1;
        if (d2) params.date2 = d2;
        if (this.filters.sklad_id) params.sklad_id = this.filters.sklad_id;
        const res = await axios.get('api/v1/report/kassa-summary', { params });
        this.data = res.data;
      } finally {
        this.loading = false;
      }
    },
    loadSklads() {
      axios.get('api/v1/sklads').then(r => { this.sklads = r.data || []; }).catch(() => {});
    },
    loadKassaCategories() {
      axios.get('api/v1/kassa-category').then(r => {
        const d = r.data;
        this.kassaCategories = Array.isArray(d) ? d : (d.data || d.items || []);
      }).catch(() => { this.kassaCategories = []; });
    },
    loadPaymentTypes() {
      axios.get('api/v1/pay-type').then(r => {
        this.paymentTypes = Array.isArray(r.data) ? r.data : (r.data.data || []);
      }).catch(() => { this.paymentTypes = []; });
    },
    loadDollarRate() {
      axios.get('api/v1/dollar-exchange-rate/last').then(r => {
        this.dollarRate = (r.data && (r.data.rate || r.data.amount)) || 0;
      }).catch(() => { this.dollarRate = 0; });
    },
    openKirim() {
      // Joriy filtrdagi sklad real id bo'lsa o'shani, aks holda birinchi skladni tanlaymiz
      let sid = this.filters.sklad_id;
      if (!sid || sid === 'all') sid = this.sklads.length ? this.sklads[0].id : '';
      this.kirim = {
        sklad_id: sid,
        pay_type_id: null,
        summa: 0,
        dollar_rate: this.dollarRate || 0,
        kassa_category_id: null,
        comment: '',
      };
      this.showKirim = true;
    },
    async submitKirim() {
      const summa = parseFloat(this.kirim.summa) || 0;
      const isDollar = Number(this.kirim.pay_type_id) === 3;

      if (!this.kirim.sklad_id) {
        this.$store.state.errors = 'Sklad tanlang';
        return;
      }
      if (!this.kirim.pay_type_id) {
        this.$store.state.errors = "To'lov turini tanlang";
        return;
      }
      if (summa <= 0) {
        this.$store.state.errors = 'Summani kiriting';
        return;
      }
      if (isDollar && (!this.kirim.dollar_rate || this.kirim.dollar_rate <= 0)) {
        this.$store.state.errors = 'Dollar kursini kiriting';
        return;
      }

      this.saving = true;
      try {
        await axios.post('api/v1/kassa-order', {
          type: 'kirim',
          sklad_id: this.kirim.sklad_id,
          kassa_category_id: this.kirim.kassa_category_id || null,
          pay_type_id: this.kirim.pay_type_id,
          // Dollar to'lovda summa dollar_summa ustuniga, aks holda summa ustuniga
          summa: isDollar ? 0 : summa,
          dollar_summa: isDollar ? summa : 0,
          dollar_rate: this.kirim.dollar_rate || 1,
          comment: this.kirim.comment || '',
        });
        this.showKirim = false;
        this.loadData();
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message
          ? e.response.data.message : (e.message || 'Xatolik');
        this.$store.state.errors = msg;
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    this.loadSklads();
    this.loadKassaCategories();
    this.loadPaymentTypes();
    this.loadDollarRate();
    this.setQuick('month');
  },
};
</script>

<style scoped>
.g-page { padding: 16px; background: #f5f7fb; min-height: 100vh; }
.content-container { max-width: 1400px; margin: 0 auto; }

.header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.page-title { font-size: 22px; font-weight: 600; color: #2d3748; display: flex; align-items: center; gap: 8px; }
.title-icon { color: #3182ce; }
.header-actions { display: flex; gap: 8px; align-items: center; }
.refresh-btn { background: white; border: 1px solid #e2e8f0; padding: 8px 12px; border-radius: 8px; cursor: pointer; }
.refresh-btn:hover { background: #edf2f7; }
.kirim-btn { background: #38a169; color: white; border: none; padding: 9px 16px; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 14px; display: flex; align-items: center; gap: 6px; }
.kirim-btn:hover { background: #2f855a; }

/* Kassaga kirim modal */
.kk-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 1050; }
.kk-modal { background: white; width: 100%; max-width: 540px; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.2); overflow: hidden; }
.kk-head { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #edf2f7; }
.kk-head h3 { margin: 0; font-size: 18px; font-weight: 600; color: #2d3748; }
.kk-close { background: none; border: none; font-size: 26px; line-height: 1; color: #a0aec0; cursor: pointer; }
.kk-close:hover { color: #4a5568; }
.kk-body { padding: 20px; }
.kk-hint { font-size: 13px; color: #718096; background: #f0fff4; border-left: 3px solid #38a169; padding: 10px 12px; border-radius: 6px; margin-bottom: 16px; }
.kk-row { margin-bottom: 14px; }
.kk-row label, .kk-cell label { display: block; font-size: 12px; color: #718096; margin-bottom: 4px; font-weight: 500; }
.kk-req { color: #e53e3e; }
.kk-cur { color: #2f855a; font-weight: 600; }
.kk-grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 14px; }
.kk-input { width: 100%; padding: 9px 11px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 14px; background: white; }
.kk-input:focus { outline: none; border-color: #38a169; }
.kk-total { font-size: 15px; color: #2d3748; text-align: right; padding-top: 8px; border-top: 1px dashed #e2e8f0; }
.kk-foot { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px; border-top: 1px solid #edf2f7; background: #f7fafc; }
.kk-btn { padding: 9px 18px; border-radius: 8px; border: none; cursor: pointer; font-weight: 600; font-size: 14px; }
.kk-btn.cancel { background: #edf2f7; color: #4a5568; }
.kk-btn.cancel:hover { background: #e2e8f0; }
.kk-btn.save { background: #38a169; color: white; }
.kk-btn.save:hover { background: #2f855a; }
.kk-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.filter-panel { background: white; padding: 16px; border-radius: 10px; margin-bottom: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
.filter-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; }
.filter-cell label { display: block; font-size: 12px; color: #718096; margin-bottom: 4px; font-weight: 500; }
.form-input { width: 100%; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 14px; background: white; }
.quick-row { display: flex; gap: 4px; flex-wrap: wrap; }
.quick-btn { padding: 7px 10px; background: #edf2f7; border: 1px solid #cbd5e0; border-radius: 6px; cursor: pointer; font-size: 12px; }
.quick-btn:hover { background: #e2e8f0; }

.balance-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
.bal-card.big { background: white; padding: 24px; border-radius: 12px; box-shadow: 0 2px 6px rgba(0,0,0,0.06); border-left: 4px solid #3182ce; }
.bal-card.big.dollar { border-left-color: #38a169; }
.bal-label { font-size: 13px; color: #718096; margin-bottom: 8px; font-weight: 500; }
.bal-value { font-size: 32px; font-weight: 700; color: #1a202c; margin-bottom: 4px; }
.bal-card.big.dollar .bal-value { color: #2f855a; }
.bal-sub { font-size: 12px; color: #a0aec0; }

.totals-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin-bottom: 16px; }
.t-card { background: white; padding: 14px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); border-left: 3px solid #cbd5e0; }
.t-card.kirim  { border-left-color: #38a169; }
.t-card.chiqim { border-left-color: #e53e3e; }
.t-card.net.positive { border-left-color: #3182ce; }
.t-card.net.negative { border-left-color: #d69e2e; }
.t-card.dollar { background: #f0fff4; }
.t-label { font-size: 12px; color: #718096; margin-bottom: 4px; }
.t-value { font-size: 18px; font-weight: 600; color: #2d3748; }

.section { background: white; padding: 16px; border-radius: 10px; margin-bottom: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
.section-title { font-size: 15px; font-weight: 600; color: #2d3748; margin-bottom: 12px; }
.g-table { width: 100%; border-collapse: collapse; }
.g-table th, .g-table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #edf2f7; font-size: 14px; }
.g-table th { background: #f7fafc; color: #4a5568; font-weight: 600; font-size: 12px; text-transform: uppercase; }
.g-table.compact td, .g-table.compact th { padding: 6px 10px; font-size: 13px; }
.g-table tbody tr:hover { background: #f7fafc; }

.pay-badge { display: inline-block; padding: 3px 8px; border-radius: 4px; font-size: 12px; font-weight: 500; }
.pay-badge.sum    { background: #ebf8ff; color: #2c5282; }
.pay-badge.dollar { background: #f0fff4; color: #276749; }

.positive { color: #38a169; }
.negative { color: #e53e3e; }
.muted    { color: #a0aec0; }

.loading-state, .empty-state { text-align: center; padding: 40px; color: #a0aec0; background: white; border-radius: 10px; }
</style>
